import { useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { useTransmettreAuCommercial, type ResultatTransmission } from '../../lib/usePedagogique';

/**
 * Le pôle pédagogique repère les personnes à placer ; positionner est le
 * métier de la relation entreprise et du recrutement. La sélection leur est
 * signalée, antenne par antenne, dans leur espace et par e-mail. Le message ne
 * porte que nom, formation et situation.
 */
export function ModaleTransmission({
  apprenantIds,
  ouverte,
  onFermer,
  onTransmis,
}: {
  apprenantIds: string[];
  ouverte: boolean;
  onFermer: () => void;
  onTransmis: (resultat: ResultatTransmission) => void;
}): JSX.Element | null {
  const transmettre = useTransmettreAuCommercial();
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!ouverte) {
      setMessage('');
      transmettre.reset();
    }
    // `transmettre` change à chaque rendu : seule l'ouverture compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  const n = apprenantIds.length;

  return (
    <Modale
      titre="Transmettre au commercial"
      sousTitre={`${n} personne${n > 1 ? 's' : ''} sélectionnée${n > 1 ? 's' : ''}`}
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <form
        onSubmit={(evenement) => {
          evenement.preventDefault();
          transmettre.mutate(
            { apprenantIds, ...(message.trim() ? { message: message.trim() } : {}) },
            { onSuccess: onTransmis },
          );
        }}
      >
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm leading-relaxed text-codialis-navy">
            La relation entreprise et le recrutement de chaque antenne concernée reçoivent la
            liste — nom, formation, situation — dans leur espace et par e-mail, avec un lien vers
            le matching. Les personnes qui ne sont plus sans entreprise n’y figurent pas.
          </p>
          <div>
            <label
              htmlFor="message-transmission"
              className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-codialis-gray"
            >
              Message (facultatif)
            </label>
            <textarea
              id="message-transmission"
              value={message}
              onChange={(evenement) => setMessage(evenement.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Disponibilités, secteur souhaité…"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
            />
          </div>
          {transmettre.error && (
            <MessageErreur
              message={
                transmettre.error instanceof ErreurApi
                  ? transmettre.error.message
                  : 'La transmission n’a pas abouti.'
              }
            />
          )}
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-4 py-3.5 sm:flex-row sm:justify-end sm:px-6">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={transmettre.isPending || n === 0}>
            {transmettre.isPending ? 'Transmission…' : 'Transmettre'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
